/**
 * Contrato único de las propuestas de los agentes (Fase 2 · Agentes).
 *
 * Fuente de verdad: la tabla `agent_proposals`
 * (supabase/migrations/20261015000000_agent_proposals.sql). Los nombres de campo van en snake_case,
 * iguales a las columnas, para que no haya mapeos intermedios. Una prueba de contrato compara estos
 * valores con los CHECK del SQL.
 *
 * Los agentes SOLO PROPONEN: una propuesta no escribe asientos, transacciones ni registros SUNAT.
 * Al aprobarla, el servicio llama al flujo contable existente. `propuesta` guarda referencias y valores
 * elegidos; NUNCA montos calculados por un LLM (los calcula el motor contable al aprobar).
 */

import { esVistaPrevia, type EntornoVistaPrevia } from '../../utils/vistaPrevia';

export const AGENTES_PROPUESTA = ['clasificador', 'conciliador', 'resumen', 'informe'] as const;
export type AgentePropuesta = (typeof AGENTES_PROPUESTA)[number];

export const ESTADOS_PROPUESTA = ['pendiente', 'aprobada', 'rechazada', 'expirada'] as const;
export type EstadoPropuesta = (typeof ESTADOS_PROPUESTA)[number];

/** Transiciones que admite agent_proposal_transition (espejo del SQL). `aprobada` se aplica con mark_applied. */
export const TRANSICIONES_PROPUESTA: Readonly<Record<EstadoPropuesta, readonly EstadoPropuesta[]>> = {
  pendiente: ['aprobada', 'rechazada', 'expirada'],
  aprobada: [],
  rechazada: [],
  expirada: [],
};

export function transicionPermitida(desde: EstadoPropuesta, hacia: EstadoPropuesta): boolean {
  return TRANSICIONES_PROPUESTA[desde].includes(hacia);
}

/** Objeto JSON plano (sin montos calculados por LLM). */
export type JsonObject = { [clave: string]: unknown };

export interface AgentProposal {
  id: string;
  /** auth.uid() de la firma. Lo fija la base de datos, nunca el cliente. */
  firm_id: string;
  company_id: string;
  /** YYYY-MM. */
  periodo: string;
  agente: AgentePropuesta;
  /** Tipo de propuesta; cada agente define los suyos (p. ej. 'clasificacion', 'conciliacion'). */
  tipo: string;
  /** Identificador de lo propuesto (id de transacción, de movimiento bancario…). Nunca un RUC. */
  entidad_id: string;
  /** Lo que el agente propone: referencias y valores elegidos, sin cifras calculadas por un LLM. */
  propuesta: JsonObject;
  /** Lo que el usuario realmente eligió al aprobar (puede diferir de `propuesta`). */
  valor_final: JsonObject | null;
  /** 0–1. La acción se decide con confianzaToAccion(). */
  confianza: number;
  /** Texto en español (Perú) para el usuario; sin RUC, cuentas bancarias ni montos exactos si va a un LLM. */
  explicacion: string;
  estado: EstadoPropuesta;
  /** Quién la creó: p. ej. 'clasificador@1'. */
  creado_por_agente: string;
  /** auth.uid() de quien revisó; lo fija el servidor. */
  revisado_por: string | null;
  /** ISO 8601. */
  revisado_en: string | null;
  /** ISO 8601. Nulo en una aprobada = pendiente de aplicar (reintentable). */
  aplicada_en: string | null;
  /** Motivo del último fallo al aplicar. */
  error: string | null;
  creado_en: string;
  expira_en: string;
}

/** Lo que un agente entrega al proponer. firm_id, id, estado y fechas los pone la base de datos. */
export type NuevaPropuesta = Pick<
  AgentProposal,
  'company_id' | 'periodo' | 'agente' | 'tipo' | 'entidad_id' | 'propuesta' | 'confianza' | 'explicacion' | 'creado_por_agente'
>;

/** Aprobada y todavía sin aplicar al flujo contable: se puede reintentar. */
export function pendienteDeAplicar(p: Pick<AgentProposal, 'estado' | 'aplicada_en'>): boolean {
  return p.estado === 'aprobada' && p.aplicada_en === null;
}

// ─── Auditoría (formato de changes_json — lo consumen todos los agentes) ───────────────────────────

/** audit_events.entity_type de las propuestas. */
export const AUDIT_ENTITY_TYPE_PROPUESTA = 'agent_proposal';

/** Acciones que admite audit_events.action en producción (CHECK verificado). Ninguna ventana las amplía. */
export type AuditAction =
  | 'created' | 'updated' | 'posted' | 'exported' | 'viewed'
  | 'deleted_soft' | 'login' | 'logout' | 'password_recovery' | 'agent_run';

/**
 * Tipo de operación: va SIEMPRE en changes_json.operacion.
 *  proponer → created · aprobar/rechazar/corregir/aplicar/error_aplicacion/expirar → updated
 *  ejecutar (un agente corre) · generar (un agente genera su análisis/informe) → agent_run
 *  exportar (PDF/Excel) → exported
 */
export const AUDIT_OPERACIONES = [
  'proponer', 'aprobar', 'rechazar', 'corregir', 'aplicar', 'error_aplicacion', 'expirar', 'ejecutar', 'generar', 'exportar',
] as const;
export type AuditOperacion = (typeof AUDIT_OPERACIONES)[number];

const ACCION_POR_OPERACION: Readonly<Record<AuditOperacion, AuditAction>> = {
  proponer: 'created',
  aprobar: 'updated',
  rechazar: 'updated',
  corregir: 'updated',
  aplicar: 'updated',
  error_aplicacion: 'updated',
  expirar: 'updated',
  ejecutar: 'agent_run',
  generar: 'agent_run',
  exportar: 'exported',
};

export function auditActionDe(operacion: AuditOperacion): AuditAction {
  return ACCION_POR_OPERACION[operacion];
}

/**
 * Claves que nunca viajan a audit_events.changes_json de un agente.
 *  - Financieras / PII: se detectan como palabra completa dentro de la clave (monto_pagado, montoPagado, ruc_proveedor…),
 *    para no descartar claves legítimas como `accion` o `estructura`.
 *  - Contenido de la propuesta y textos libres: solo cuando son la clave exacta (`con_error` sí pasa).
 */
const TOKEN_SENSIBLE = /(^|_)(monto|importe|total|saldo|debe|haber|ruc|dni|cci|iban|email|telefono|cuenta_bancaria)(_|$)/;
const CLAVE_EXACTA_PROHIBIDA = /^(error|explicacion|propuesta|valor_final|entidad_id)$/;
const SECUENCIA_SENSIBLE = /\b\d{8,20}\b/g;

function claveProhibida(clave: string): boolean {
  const k = clave.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[-.\s]+/g, '_').toLowerCase();
  return CLAVE_EXACTA_PROHIBIDA.test(k) || TOKEN_SENSIBLE.test(k);
}

function limpiar(valor: unknown, profundidad = 0): unknown {
  if (typeof valor === 'string') return valor.replace(SECUENCIA_SENSIBLE, '[REDACTADO]');
  if (profundidad >= 4) return undefined;
  if (Array.isArray(valor)) return valor.map((v) => limpiar(v, profundidad + 1)).filter((v) => v !== undefined);
  if (valor !== null && typeof valor === 'object') {
    const salida: JsonObject = {};
    for (const [k, v] of Object.entries(valor as JsonObject)) {
      if (claveProhibida(k)) continue;
      const limpio = limpiar(v, profundidad + 1);
      if (limpio !== undefined) salida[k] = limpio;
    }
    return salida;
  }
  return valor;
}

/**
 * Quita de un texto libre (mensajes de error que se guardan en agent_proposals.error o se muestran) todo lo
 * que no debe persistir: RUC / cuentas bancarias / DNI (secuencias de 8+ dígitos) y montos (S/ 1,180.00,
 * 1,180.00, 1180.00). Los códigos PCGE (3–4 dígitos) y los períodos (2026-03) se conservan.
 */
export function redactarTextoSensible(texto: string): string {
  return texto
    .replace(/S\/\s?-?\d[\d.,]*/g, 'S/ [monto]')
    .replace(/\b\d{8,20}\b/g, '[REDACTADO]')
    .replace(/\b\d{1,3}(?:,\d{3})+(?:\.\d+)?\b/g, '[monto]')
    .replace(/\b\d+\.\d{2}\b/g, '[monto]');
}

/**
 * Entorno desde el que se escribió la fila de auditoría de un agente. 'vista_previa' = vista previa de Vercel
 * (misma base de datos que producción): permite distinguir esas filas al auditar.
 */
export const ENTORNOS_AUDITORIA = ['produccion', 'vista_previa'] as const;
export type EntornoAuditoria = (typeof ENTORNOS_AUDITORIA)[number];

/** Se calcula con el MISMO helper que bloquea las escrituras en vista previa (esVistaPrevia). */
export function entornoAuditoria(entorno?: EntornoVistaPrevia): EntornoAuditoria {
  return esVistaPrevia(entorno) ? 'vista_previa' : 'produccion';
}

export interface AgentAuditChanges extends JsonObject {
  operacion: AuditOperacion;
  entorno: EntornoAuditoria;
}

/**
 * Arma el changes_json de un evento de agente: `operacion` y `entorno` siempre presentes (el detalle no puede
 * pisarlos), y del detalle se descartan las claves con montos, RUC, cuentas bancarias o textos libres, y se
 * redactan secuencias de 8+ dígitos. `entorno` se calcula con esVistaPrevia(); el parámetro solo existe para pruebas.
 */
export function construirChangesJsonAgente(
  operacion: AuditOperacion,
  detalle: JsonObject = {},
  entorno?: EntornoVistaPrevia,
): AgentAuditChanges {
  const limpio = limpiar(detalle) as JsonObject;
  return { ...limpio, operacion, entorno: entornoAuditoria(entorno) };
}
